import React, { useEffect, useState } from 'react';
import { deleteCompletedTask, downloadCompletedTasks, editCompletedTask, getAllCompletedTasks } from '../../app/api';
import { useCompletedTasksContext } from '../../context/CompletedTasksContext';
import { Button, DatePicker, Dropdown, Input, Modal, Table, Form, Select } from 'antd';
import { MoreOutlined, FilterOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import moment from 'moment';

const { RangePicker } = DatePicker;

const DoneWorks = () => {
  const { completedTasks, setCompletedTasks } = useCompletedTasksContext();
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState([]);
  const [filterRange, setFilterRange] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editData, setEditData] = useState(null); 
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(false);

  const [form] = Form.useForm();

  const serviceTypes = [
    "Շարժիչի վերանորոգում",
    "Յուղի փոխարինում",
    "Տրանսմիսիոն համակարգի սպասարկում",
    "Էլեկտրական համակարգի սպասարկում",
    "Սարքավորման և էլեկտրոնիկայի վերանորոգում",
    "Անիվների ու դիսկերի վերանորոգում",
    "Բենզինային կամ դիզելային շարժիչների մաքրման և սպասարկում",
    "Ամորտիզացիոն համակարգի վերանորոգում",
    "Կիրառական մասերի պարբերական սպասարկում"
  ];

  const showFilterModal = () => setIsFilterModalOpen(true);
  const handleFilterCancel = () => setIsFilterModalOpen(false);

  const handleResetFilter = () => {
    setDateRange([]);
    setFilterRange([]);
  };

  const handleApplyFilter = () => {
    setFilterRange(dateRange);
    setIsFilterModalOpen(false);
  };

  const getCompletedTasks = async () => {
      try {
        setLoading(true);
          const data = {
            search,
            startDate: dateRange.length ? dateRange[0].format("YYYY-MM-DD") : "",
            endDate: dateRange.length ? dateRange[1].format("YYYY-MM-DD") : "",
            page,
            limit
          }
          const allData = await getAllCompletedTasks(data);
          const all_transactions = allData.data;
          if (allData.status === 200) {
            setCompletedTasks(all_transactions?.data);
            setTotal(all_transactions.total);
            if (all_transactions.data.length === 0 && !search) {
              setDisabled(true);
            } else {
              setDisabled(false);
            }
          }
      } catch(err) {
        console.log("Error", err);
      } finally {
        setLoading(false);
      }
  };

  const handleEditModalCancel = () => {
    setIsEditModalOpen(false);
    setEditData(null);
    form.resetFields();
  }

  const onEdit = (data) => {
    setEditData(data);
    setIsEditModalOpen(true); 
    form.setFieldsValue({
      username: data.username,
      mobile: data.mobile,
      work_name: data.work_name || [],
      auto_name: data.auto_name,
      car_number: data.car_number ,
      totalAmount: data.totalAmount,
      description: data.description,
      date: moment(data.date, 'YYYY-MM-DD'),
    });
  }

  const onDelete = (id) => {
    Modal.confirm({
      title: 'Դուք համոզվա՞ծ եք',
      okText: 'Այո',
      okType: 'danger',
      cancelText: 'Ոչ',
      onOk: async () => {
        try {
          const result = await deleteCompletedTask(id);
          if (result.status === 200) {
            setCompletedTasks(prevTransactions => prevTransactions.filter(item => item.id !== id));
          }
        } catch (error) {
          console.error('Error deleting transaction:', error);
        }
      },
      onCancel() {
        console.log('Delete operation canceled');
      },
    });
  }

  const onDownloadPDF = async (id) => {
    try {
        console.log(`Downloading PDF for task ID ${id}...`);
        await downloadCompletedTasks(id);
    } catch (error) {
        console.error("Error downloading PDF:", error);
    }
  };

  const columns = [
    {
      title: '№',
      dataIndex: 'index',
      key: 'index',
      render: (text, record, index) => (page - 1) * limit + index + 1,
    },
    {
      title: 'Սեփականատեր',
      dataIndex: 'username',
      key: 'username',
      render: (text, record) => (
        <span
          onClick={() => window.location.href = `/done/${record.id}`}
          style={{ cursor: 'pointer' }}
        >
          {text}
        </span>
      )
    },
    {
      title: 'Հեռախոսահամար',
      dataIndex: 'mobile',
      key: 'mobile',
    },
    {
      title: 'Անվանում',
      dataIndex: 'work_name',
      key: 'work_name',
      render: (workNames) => (
        <>
          {workNames?.filter(name => name.trim() !== "").map((name, index) => (
            <div key={index}>
              {name.length > 50 ? name.substring(0, 50) + '...' : name}, 
            </div>
          ))}
        </>
      ),
      onCell: () => ({
        style: { 
          maxWidth: 300, 
          wordWrap: 'break-word', 
          whiteSpace: 'normal' 
        }
      })    
    },
    {
      title: 'Ավտոմեքենայի անուն',
      dataIndex: 'auto_name',
      key: 'auto_name',
    },
    {
      title: 'Ավտոմեքենայի համարանիշ',
      dataIndex: 'car_number',
      key: 'car_number',
    },
    {
      title: 'Ընդհանուր գումար',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (text) => `${text} դրամ`,
    },
    {
      title: 'Նկարագրություն',
      dataIndex: 'description',
      key: 'description',
      render: (text) => {
        if (text && text.length > 50) {
          return text.substring(0, 50) + '...';
        }
        return text;
      },
      onCell: () => ({
        style: { 
          maxWidth: 200, 
          wordWrap: 'break-word', 
          whiteSpace: 'normal' 
        }
      })
    },
    {
      title: 'Ամսաթիվ',
      dataIndex: 'date',
      key: 'date',
      render: (text) => new Date(text).toLocaleDateString()
    },
    {
      title: 'Գործողություններ',
      key: 'actions',
      align: 'center',
      render: (_, record) => {
        const menuItems = [
          {
            key: 'edit',
            label: '✏️ Խմբագրել',
            onClick: () => onEdit(record),
          },
          {
            key: 'delete',
            label: '🗑️ Ջնջել',
            danger: true,
            onClick: () => onDelete(record.id),
          },
          // {
          //   key: 'download-pdf',
          //   label: '📄 Բեռնել PDF',
          //   onClick: () => onDownloadPDF(record.id),
          // },
        ];
  
        return (
          <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];
  
  const handleTableChange = (pagination) => {
    setPage(pagination.current);
    setLimit(pagination.pageSize);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    if (e.target.value === "") {
      getCompletedTasks();
    }
  };

  const handleEdit = async  (values) => {
    const newData = {
      ...values,
      date: values.date.format('YYYY-MM-DD'),
    };

    const response = await editCompletedTask(editData.id, newData);

    if (response.status === 200) {
      setIsEditModalOpen(false);
      form.resetFields();
      setEditData(null);
      getCompletedTasks();
    }
  }

  useEffect(() => {
      if (search) {
        const delayDebounceFn = setTimeout(() => {
          getCompletedTasks();
        }, 500);
        return () => clearTimeout(delayDebounceFn);
      } else {
        getCompletedTasks();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page, filterRange]);

  return <div style={{ padding: 20 }}>
    <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
        <Input
          placeholder="Որոնել..."
          prefix={<SearchOutlined />}
          value={search}
          onChange={handleSearchChange}
          disabled={disabled}
          allowClear
          style={{ width: 200 }}
        />
        <Button icon={<FilterOutlined />} onClick={showFilterModal}>
          Ֆիլտր
        </Button>
        {filterRange.length > 0 && (
          <Button icon={<ReloadOutlined />} onClick={handleResetFilter}>
            Մաքրել ֆիլտրը
          </Button>
        )}
    </div>

    <Modal title="Ֆիլտր" open={isFilterModalOpen} onCancel={handleFilterCancel} footer={null}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <RangePicker format="YYYY-MM-DD" value={dateRange.length ? dateRange : null} onChange={(dates) => setDateRange(dates || [])} placeholder={["Սկիզբ", "Վերջ"]} />
          <Button type="primary" onClick={handleApplyFilter} block>
            Հաստատել
          </Button>
        </div>
    </Modal>

    <Modal
      title="Խմբագրել Տեղեկություն"
      open={isEditModalOpen}
      onCancel={handleEditModalCancel}
      footer={null}
    >
      <Form layout="vertical" onFinish={handleEdit} form={form}>
        <Form.Item label="Սեփականատեր" name="username">
          <Input />
        </Form.Item>
        <Form.Item label="Հեռախոսահամար" name="mobile">
          <Input />
        </Form.Item>
        <Form.Item
          name="work_name"
          label="Անվանում"
        >
          <Select
            mode="multiple"
            placeholder="Ընտրեք ծառայության անվանումը"
            defaultValue={editData?.work_name || []}
            options={serviceTypes.map((type) => ({ value: type, label: type }))}
            allowClear
          />
        </Form.Item>
        {/* <Form.Item label="Անվանում" name="work_name">
          <Input />
        </Form.Item> */}
        <Form.Item label="Ավտոմեքենայի անուն" name="auto_name">
          <Input />
        </Form.Item>
        <Form.Item label="Ավտոմեքենայի համարանիշ" name="car_number">
          <Input />
        </Form.Item>
        <Form.Item label="Ընդհանուր գումար" name="totalAmount">
          <Input />
        </Form.Item>
        <Form.Item label="Նկարագրություն" name="description">
          <Input />
        </Form.Item>
        <Form.Item label="Ամսաթիվ" name="date" >
          <DatePicker
            format="YYYY-MM-DD"
            placeholder="Օրինակ՝ 2025-01-27"
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Button type="primary" htmlType="submit" block >
          Խմբագրել
        </Button>
      </Form>
    </Modal>

    <Table
        columns={columns}
        dataSource={completedTasks}
        rowKey="id"
        pagination={{
          current: page,
          pageSize: limit,
          total: total
        }}
        loading={loading}
        scroll={{ x: 'max-content' }}
        onChange={handleTableChange}
    />
  </div>;
};

export default DoneWorks;
